import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import DashboardShell, { StatCard } from "./DashboardShell";
import { Calendar, MapPin, AlertTriangle, FileDown, Calculator } from "lucide-react";

export default function ExhibitorDashboard() {
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get("/my/bookings").then((r) => setBookings(r.data));
  }, []);

  const paid = bookings.filter((b) => b.payment_status === "paid");
  const totalBudget = paid.reduce((s, b) => s + (b.amount || 0), 0);

  // Detect date conflicts
  const conflicts = new Set();
  for (let i = 0; i < paid.length; i++) {
    for (let j = i + 1; j < paid.length; j++) {
      const a = paid[i].event, b = paid[j].event;
      if (!a || !b) continue;
      const a1 = new Date(a.start_date), a2 = new Date(a.end_date);
      const b1 = new Date(b.start_date), b2 = new Date(b.end_date);
      if (a1 <= b2 && b1 <= a2) { conflicts.add(paid[i].id); conflicts.add(paid[j].id); }
    }
  }

  return (
    <DashboardShell title="Mon agenda de saison" subtitle="Tous vos événements réservés en un coup d'œil">
      <div className="flex justify-end mb-6">
        <Link
          to="/dashboard/budget"
          data-testid="link-budget-planner"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-ink text-cream hover:bg-ink/85 rounded-none"
        >
          <Calculator className="h-3.5 w-3.5" /> Budget prévisionnel
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Stands réservés" value={paid.filter(b => b.item_type === "booth").length} />
        <StatCard label="En attente" value={bookings.filter(b => !["paid", "refunded", "partially_refunded", "expired"].includes(b.payment_status)).length} />
        <StatCard label="Conflits" value={conflicts.size} accent={conflicts.size > 0} />
        <StatCard label="Budget total" value={`${totalBudget.toFixed(0)}€`} accent />
      </div>

      <div className="bg-white border border-ink/10 rounded-md">
        <div className="p-5 border-b border-ink/10">
          <h2 className="font-heading font-medium text-lg text-ink">Mes réservations</h2>
        </div>
        {bookings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-muted-foreground mb-4">Pas encore de réservation.</p>
            <Link to="/events" className="text-accent font-semibold hover:underline">Explorer les événements →</Link>
          </div>
        ) : (
          <div className="divide-y divide-ink/10">
            {bookings.map((b) => (
              <div key={b.id} className="p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-primary truncate">{b.item_title}</p>
                    {conflicts.has(b.id) && (
                      <span className="inline-flex items-center gap-1 text-xs text-accent bg-cream-200 px-2 py-0.5 rounded">
                        <AlertTriangle className="h-3 w-3" /> Conflit
                      </span>
                    )}
                  </div>
                  {b.event && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(b.event.start_date).toLocaleDateString("fr-FR")}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{b.event.city}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className="font-heading font-black text-ink">{b.amount?.toFixed(2)}€</p>
                    <p data-testid={`booking-status-${b.id}`} className={`text-xs ${b.payment_status === "paid" ? "text-trust" : ["refunded", "partially_refunded", "expired"].includes(b.payment_status) ? "text-ink/50" : "text-accent"}`}>
                      {b.payment_status === "paid" ? "Confirmé"
                        : b.payment_status === "refunded" ? "Remboursé"
                        : b.payment_status === "partially_refunded" ? "Remboursé partiellement"
                        : b.payment_status === "expired" ? "Expiré"
                        : "En attente"}
                    </p>
                  </div>
                  {b.payment_status === "paid" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/invoice.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`invoice-download-${b.id}`}
                      title="Télécharger la facture PDF"
                      className="p-2 hover:bg-cream-100 rounded-md text-ink/70 hover:text-ink"
                    >
                      <FileDown className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
